import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { ArrowUpRight, ArrowDownRight, TrendingUp, BedDouble, Sparkles, Users, Copy, ExternalLink, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const ADMIN_ROLES = new Set(["hotel_admin", "super_admin", "property_manager", "revenue_manager"]);

function fmtINR(n) {
  if (!n && n !== 0) return "—";
  if (n >= 100000) return "₹" + (n / 100000).toFixed(2) + "L";
  if (n >= 1000) return "₹" + (n / 1000).toFixed(1) + "k";
  return "₹" + Math.round(n);
}

function KpiCard({ label, value, delta, suffix = "%", positive, icon: Icon }) {
  const up = positive === undefined ? delta >= 0 : positive;
  return (
    <div className="bg-[#12182B] border border-white/5 rounded-xl p-6 hover:border-[#D4AF37]/25 transition" data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-white/50">{label}</div>
        {Icon && (
          <div className="w-8 h-8 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/15 flex items-center justify-center text-[#D4AF37]">
            <Icon className="w-4 h-4" strokeWidth={1.5} />
          </div>
        )}
      </div>
      <div className="font-display text-3xl mt-4">{value}</div>
      {delta !== undefined && delta !== null && (
        <div className={`text-xs mt-2 inline-flex items-center gap-1 ${up ? "text-emerald-400" : "text-orange-400"}`}>
          {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(delta)}{suffix} vs prev
        </div>
      )}
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const bookingUrl = user?.hotel_id ? `${window.location.origin}/book/${user.hotel_id}` : "";
  const isAdmin = ADMIN_ROLES.has(user?.role);

  function copyLink() {
    navigator.clipboard.writeText(bookingUrl);
    toast.success("Booking link copied");
  }

  function shareWhatsApp() {
    const msg = `Book directly with us — zero commission, best rates guaranteed ✨\n\n${bookingUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !stats) {
    return <div className="text-white/50" data-testid="overview-loading">Loading dashboard…</div>;
  }
  const k = stats.kpis;
  const occByType = stats.room_type_occupancy;

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Overview</div>
          <h1 className="font-display text-3xl md:text-4xl">This month at a glance</h1>
        </div>
        <div className="text-sm text-white/50">Total rooms: <span className="text-white font-medium">{stats.total_rooms}</span></div>
      </div>

      {/* Direct booking link — admin-only */}
      {isAdmin && (
      <div className="bg-gradient-to-br from-[#12182B] to-[#1A2235] border border-[#D4AF37]/20 rounded-xl p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="booking-link-card">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Your direct booking link</div>
          <div className="font-mono text-sm text-white/80 truncate" data-testid="booking-link-url">{bookingUrl}</div>
          <div className="text-xs text-white/50 mt-2">Share on WhatsApp, Instagram, or your website — zero commission on every booking.</div>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <button onClick={copyLink} className="btn-ghost-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="copy-booking-link">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button onClick={shareWhatsApp} className="rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/25 transition font-medium" data-testid="share-whatsapp">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </button>
          <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="open-booking-link">
            <ExternalLink className="w-3.5 h-3.5" /> Preview
          </a>
        </div>
      </div>
      )}

      {isAdmin && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard label="Revenue" value={fmtINR(k.revenue)} delta={k.revenue_delta_pct} icon={TrendingUp} />
        <KpiCard label="Occupancy" value={`${k.occupancy}%`} delta={k.occupancy_delta_pts} suffix=" pts" icon={BedDouble} />
        <KpiCard label="ADR" value={fmtINR(k.adr)} delta={k.adr_delta_pct} icon={Sparkles} />
        <KpiCard label="RevPAR" value={fmtINR(k.revpar)} delta={k.revpar_delta_pct} icon={Users} />
      </div>
      )}

      {isAdmin && (
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid="chart-monthly-revenue">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-xl">Monthly Revenue (₹ Lakhs)</h3>
            <div className="text-xs text-white/50">Last 7 months</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.monthly_revenue_series} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#273043" strokeOpacity={0.3} vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0a0f1c", border: "1px solid #273043", borderRadius: 8 }} labelStyle={{ color: "#D4AF37" }} />
                <Area type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid="channel-distribution">
          <h3 className="font-display text-xl mb-6">Channel Distribution</h3>
          <div className="space-y-4">
            {stats.channel_distribution.slice(0, 6).map((c) => (
              <div key={c.channel}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-white/80">{c.channel}</span>
                  <span className="text-white/50">{c.pct}%</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded overflow-hidden">
                  <div className="h-full bg-[#D4AF37]" style={{ width: `${c.pct}%` }} />
                </div>
              </div>
            ))}
            {stats.channel_distribution.length === 0 && <div className="text-white/50 text-sm">No channel data yet.</div>}
          </div>
        </div>
      </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid="occupancy-by-type">
          <h3 className="font-display text-xl mb-6">Occupancy by Room Type</h3>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={occByType} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid stroke="#273043" strokeOpacity={0.3} vertical={false} />
                <XAxis dataKey="room_type" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip contentStyle={{ background: "#0a0f1c", border: "1px solid #273043", borderRadius: 8 }} />
                <Bar dataKey="occupancy" fill="#D4AF37" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid="todays-arrivals">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-xl">Upcoming Arrivals</h3>
            <span className="text-xs text-white/40">Next 48h</span>
          </div>
          <div className="space-y-4">
            {stats.todays_arrivals.map((r) => {
              const initials = r.guest_name.split(" ").map(x => x[0]).slice(0, 2).join("");
              return (
                <div key={r.id} className="flex items-start gap-3 pb-3 border-b border-white/5 last:border-none">
                  <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] text-xs font-semibold">{initials}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.guest_name}</div>
                    <div className="text-xs text-white/50 truncate">{r.room_type} · {r.nights}N</div>
                  </div>
                  <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded ${
                    r.status === "checked_in" ? "bg-emerald-500/10 text-emerald-300" :
                    r.status === "confirmed" ? "bg-[#D4AF37]/10 text-[#D4AF37]" :
                    "bg-orange-500/10 text-orange-300"}`}>{r.status.replace("_", " ")}</span>
                </div>
              );
            })}
            {stats.todays_arrivals.length === 0 && <div className="text-sm text-white/50">No arrivals in the next 48 hours.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
